import { PDFDocument, type PDFFont, type PDFForm, type PDFPage, rgb, StandardFonts } from 'pdf-lib';

const REGISTRATION_TEMPLATE_URL = '/assets/pdf-template.pdf';
const PDF_HEADER = '%PDF-';
const TEXT_BLACK = rgb(0, 0, 0);
const HEADER_BLUE = rgb(0.1, 0.1, 0.7);
const ACCENT_BLUE = rgb(0.5, 0.5, 0.8);
const LIGHT_HEADER = rgb(0.95, 0.95, 1);
const MUTED_TEXT = rgb(0.3, 0.3, 0.3);
const SECTION_TEXT = rgb(0.3, 0.3, 0.5);

interface RepresentativeRow {
  id: string;
  role: string;
  name: string;
  address: string;
}

interface FormData {
  countryOfResidence: string;
  personalId: string;
  otherCitizenship: boolean;
  citizenshipCountries: string;
  firstName: string;
  lastName: string;
  companyName: string;
  careOf: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  phone: string;
  mobile: string;
  email: string;
  registrationNumber: string;
  foundingDate: string;
  companyType: string;
  taxNumber: string;
  financialYearStart: string;
  financialYearEnd: string;
  financialYearType: string;
  customFinancialYearPeriod: string;
  boardMembers: string[];
  ceo: string;
  shareCapitalAmount: string;
  higherShareCapital: boolean;
  shareSplit: boolean;
  representativesTable: RepresentativeRow[];
  signatureType: string;
  signatureDetails: string;
  signaturePersons: string[];
  bankName: string;
  accountNumber: string;
  authorizedPersons: string[];
  hasReservations: boolean;
  reservationDetails: string;
  hasAuditor: boolean;
  auditorName: string;
  auditorContact: string;
  additionalInfo: string;
  deliveryEmail: string;
  termsAccepted: boolean;
  privacyAccepted: boolean;
  dataComplete: boolean;
  submitNow: boolean;
  digitalSignature: string;
  submitConfirmation: boolean;
  [key: string]: unknown;
}

interface DrawTextResources {
  font: PDFFont;
  boldFont: PDFFont;
  italicFont: PDFFont;
}

interface FallbackDocumentContext extends DrawTextResources {
  page: PDFPage;
  fontSize: number;
  titleSize: number;
  subtitleSize: number;
}

interface LabeledField {
  label: string;
  value: string;
}

function buildFullName(formData: FormData): string {
  return `${formData.firstName || ''} ${formData.lastName || ''}`.trim();
}

function buildCompanyAddress(formData: FormData): string {
  return `${formData.streetAddress || ''}, ${formData.postalCode || ''} ${formData.city || ''}`.trim();
}

function getCurrentIsoDate(): string {
  return new Date().toISOString().split('T')[0] || '';
}

async function fetchTemplateBytes(): Promise<ArrayBuffer | null> {
  const response = await fetch(REGISTRATION_TEMPLATE_URL, {
    headers: {
      Accept: 'application/pdf, application/octet-stream;q=0.9, */*;q=0.8',
    },
  });

  if (!response.ok) {
    console.error(
      `Template request failed with ${response.status} ${response.statusText} for ${response.url || REGISTRATION_TEMPLATE_URL}`,
    );
    return null;
  }

  const pdfBytes = await response.arrayBuffer();
  const headerBytes = new Uint8Array(pdfBytes).slice(0, PDF_HEADER.length);
  const pdfHeader = new TextDecoder('ascii').decode(headerBytes);

  if (pdfHeader === PDF_HEADER) {
    return pdfBytes;
  }

  const contentType = response.headers.get('content-type') || 'unknown content-type';
  console.error(
    `Template at ${response.url || REGISTRATION_TEMPLATE_URL} is not a valid PDF (${contentType}). Received header: ${JSON.stringify(pdfHeader)}`,
  );
  return null;
}

function setTextFieldIfPresent(form: PDFForm, name: string, value: string): void {
  try {
    form.getTextField(name).setText(value);
  } catch {
    // Ignore missing fields in varying template versions.
  }
}

function checkBoxIfPresent(form: PDFForm, name: string): void {
  try {
    form.getCheckBox(name).check();
  } catch {
    // Ignore missing fields in varying template versions.
  }
}

function fillCompanyType(form: PDFForm, companyType: string): void {
  const normalizedType = companyType.toLowerCase();

  if (normalizedType.includes('aktiebolag') || normalizedType.includes('ab')) {
    checkBoxIfPresent(form, 'Form[0].Sida1[0].subA[0].subBolagform01[0].krsAktiebolag[0]');
    return;
  }

  if (normalizedType.includes('handelsbolag')) {
    checkBoxIfPresent(form, 'Form[0].Sida1[0].subA[0].subBolagform01[0].krsHandelsbolag[0]');
    return;
  }

  if (normalizedType.includes('enskild')) {
    checkBoxIfPresent(form, 'Form[0].Sida1[0].subA[0].subBolagform01[0].krsEnskildNaring[0]');
    return;
  }

  if (normalizedType.includes('ekonomisk')) {
    checkBoxIfPresent(form, 'Form[0].Sida1[0].subA[0].subBolagform01[0].krsEkonomiskForen[0]');
    return;
  }

  setTextFieldIfPresent(
    form,
    'Form[0].Sida1[0].subA[0].subBolagform01[0].txtAnnanForm[0]',
    companyType,
  );
}

function fillRepresentatives(form: PDFForm, formData: FormData): void {
  const representatives = formData.representativesTable.filter((representative) =>
    representative.name.trim(),
  );

  for (const [index, representative] of representatives.slice(0, 3).entries()) {
    const fieldIndex = String(index + 1).padStart(2, '0');
    const shareAmount = formData.shareCapitalAmount
      ? Math.floor(
          Number.parseInt(formData.shareCapitalAmount, 10) / representatives.length,
        ).toString()
      : '';

    setTextFieldIfPresent(
      form,
      `Form[0].Sida1[0].subD[0].subDelAgrUppg${fieldIndex}[0].Namn[0]`,
      representative.name || '',
    );
    setTextFieldIfPresent(
      form,
      `Form[0].Sida1[0].subD[0].subDelAgrUppg${fieldIndex}[0].txtPnrOrnrVATnr[0]`,
      representative.address || '',
    );
    setTextFieldIfPresent(
      form,
      `Form[0].Sida1[0].subD[0].subDelAgrUppg${fieldIndex}[0].numAntalAktierAndelar[0]`,
      shareAmount,
    );
  }
}

function buildAdditionalInfoText(formData: FormData): string {
  const lines = [`Email: ${formData.email || ''}`];

  if (formData.careOf) {
    lines.push(`Care Of: ${formData.careOf}`);
  }

  if (formData.countryOfResidence) {
    lines.push(`Country of Residence: ${formData.countryOfResidence}`);
  }

  if (formData.bankName) {
    lines.push(`Bank: ${formData.bankName}`);
  }

  if (formData.accountNumber) {
    lines.push(`Account Number: ${formData.accountNumber}`);
  }

  if (formData.hasAuditor && formData.auditorName) {
    lines.push(`Auditor: ${formData.auditorName}`);

    if (formData.auditorContact) {
      lines.push(`Auditor Contact: ${formData.auditorContact}`);
    }
  }

  if (formData.hasReservations && formData.reservationDetails) {
    lines.push(`Reservations: ${formData.reservationDetails}`);
  }

  if (formData.additionalInfo) {
    lines.push(`Additional Info: ${formData.additionalInfo}`);
  }

  if (formData.signatureType) {
    lines.push(`Signature Type: ${formData.signatureType}`);
  }

  return lines.join('\n').trim();
}

function fillRegistrationForm(form: PDFForm, formData: FormData): void {
  const fullName = buildFullName(formData);
  const phoneNumber = formData.phone || formData.mobile || '';

  setTextFieldIfPresent(form, 'Form[0].Sida1[0].subKontaktperson[0].txtNamn[0]', fullName);
  setTextFieldIfPresent(
    form,
    'Form[0].Sida1[0].subKontaktperson[0].txtTelefonNr01[0]',
    phoneNumber,
  );
  setTextFieldIfPresent(form, 'Form[0].Sida1[0].subA[0].txtNamn[0]', formData.companyName || '');
  setTextFieldIfPresent(
    form,
    'Form[0].Sida1[0].subA[0].txtOrgNr[0]',
    formData.registrationNumber || formData.personalId || '',
  );

  if (formData.companyType) {
    fillCompanyType(form, formData.companyType);
  }

  setTextFieldIfPresent(
    form,
    'Form[0].Sida1[0].subA[0].subBolagform02[0].datDatumForstaBokslut[0]',
    formData.foundingDate || '',
  );
  setTextFieldIfPresent(
    form,
    'Form[0].Sida1[0].subA[0].txtPostadrPostnrOrt[0]',
    buildCompanyAddress(formData),
  );
  setTextFieldIfPresent(form, 'Form[0].Sida1[0].subA[0].txtTelefonNr01[0]', formData.phone || '');
  setTextFieldIfPresent(
    form,
    'Form[0].Sida1[0].subA[0].subForetagetsBesokAdr[0].txtForetagetsBesokAdr[0]',
    formData.streetAddress || '',
  );

  fillRepresentatives(form, formData);

  setTextFieldIfPresent(
    form,
    'Form[0].Sida2[0].subF[0].subStartdatum[0].datDatum1[0]',
    formData.financialYearStart || '',
  );
  setTextFieldIfPresent(
    form,
    'Form[0].Sida2[0].subF[0].subStartdatum[0].datDatum2[0]',
    formData.financialYearEnd || '',
  );
  setTextFieldIfPresent(
    form,
    'Form[0].Sida2[0].subOvrUppl[0].txtOvrigaUpplysn[0]',
    buildAdditionalInfoText(formData),
  );
  setTextFieldIfPresent(form, 'Form[0].Sida2[0].subKontakt[0].txtSokUnderskrift[0]', fullName);
  setTextFieldIfPresent(
    form,
    'Form[0].Sida2[0].subKontakt[0].datDatumLangt[0]',
    getCurrentIsoDate(),
  );
  setTextFieldIfPresent(form, 'Form[0].Sida2[0].subKontakt[0].txtNamnfortydl[0]', fullName);
}

function drawSectionTitle(
  context: FallbackDocumentContext,
  title: string,
  yPosition: number,
): number {
  context.page.drawText(title, {
    x: 50,
    y: yPosition,
    size: context.subtitleSize,
    font: context.boldFont,
    color: HEADER_BLUE,
  });

  context.page.drawLine({
    start: { x: 50, y: yPosition - 10 },
    end: { x: 545, y: yPosition - 10 },
    thickness: 1,
    color: ACCENT_BLUE,
  });

  return yPosition - 30;
}

function drawTwoColumnFields(
  context: FallbackDocumentContext,
  fields: LabeledField[],
  startY: number,
): number {
  let yPosition = startY;

  for (const [index, field] of fields.entries()) {
    const xPosition = 70 + (index % 2 === 0 ? 0 : 250);

    if (index % 2 === 0 && index > 0) {
      yPosition -= 25;
    }

    context.page.drawText(`${field.label}:`, {
      x: xPosition,
      y: yPosition,
      size: context.fontSize,
      font: context.boldFont,
      color: TEXT_BLACK,
    });
    context.page.drawText(field.value, {
      x: xPosition + 120,
      y: yPosition,
      size: context.fontSize,
      font: context.font,
      color: TEXT_BLACK,
    });
  }

  return yPosition - 40;
}

function drawFallbackHeader(context: FallbackDocumentContext): void {
  context.page.drawRectangle({
    x: 0,
    y: 780,
    width: 595.28,
    height: 60,
    color: LIGHT_HEADER,
  });
  context.page.drawRectangle({
    x: 50,
    y: 790,
    width: 40,
    height: 40,
    color: HEADER_BLUE,
  });
  context.page.drawText('Company Registration Form', {
    x: 100,
    y: 810,
    size: context.titleSize,
    font: context.boldFont,
    color: HEADER_BLUE,
  });

  const currentDate = new Date().toLocaleDateString();
  const refNumber = `REF-${Math.floor(Math.random() * 900000) + 100000}`;

  context.page.drawText(`Reference: ${refNumber}`, {
    x: 100,
    y: 790,
    size: context.fontSize,
    font: context.font,
    color: TEXT_BLACK,
  });
  context.page.drawText(`Date: ${currentDate}`, {
    x: 400,
    y: 790,
    size: context.fontSize,
    font: context.font,
    color: TEXT_BLACK,
  });
}

function drawFallbackDescription(context: FallbackDocumentContext, yPosition: number): number {
  context.page.drawText(
    'The contact person is the orderer and the one to whom contracts and other documents',
    {
      x: 50,
      y: yPosition,
      size: context.fontSize - 1,
      font: context.italicFont,
      color: MUTED_TEXT,
    },
  );
  context.page.drawText(
    'will be sent. This person does not have to be the same as the company buyer.',
    {
      x: 50,
      y: yPosition - 15,
      size: context.fontSize - 1,
      font: context.italicFont,
      color: MUTED_TEXT,
    },
  );

  return yPosition - 40;
}

function drawFallbackSubsectionTitle(
  context: FallbackDocumentContext,
  title: string,
  yPosition: number,
): number {
  context.page.drawText(title, {
    x: 50,
    y: yPosition,
    size: context.fontSize + 1,
    font: context.boldFont,
    color: SECTION_TEXT,
  });

  return yPosition - 20;
}

function drawFallbackFooter(context: FallbackDocumentContext): void {
  context.page.drawRectangle({
    x: 0,
    y: 0,
    width: 595.28,
    height: 40,
    color: LIGHT_HEADER,
  });
  context.page.drawText('Page 1 of 1', {
    x: 275,
    y: 15,
    size: 10,
    font: context.font,
    color: TEXT_BLACK,
  });
  context.page.drawText('This document was automatically generated and may need verification.', {
    x: 50,
    y: 15,
    size: 8,
    font: context.italicFont,
    color: rgb(0.5, 0.5, 0.5),
  });
}

function buildFallbackContext(
  page: PDFPage,
  resources: DrawTextResources,
): FallbackDocumentContext {
  return {
    page,
    ...resources,
    fontSize: 11,
    titleSize: 18,
    subtitleSize: 14,
  };
}

function getPersonalFields(formData: FormData): LabeledField[] {
  return [
    { label: 'First Name', value: formData.firstName || 'N/A' },
    { label: 'Last Name', value: formData.lastName || 'N/A' },
    { label: 'Personal ID', value: formData.personalId || 'N/A' },
    { label: 'Country of Residence', value: formData.countryOfResidence || 'N/A' },
    { label: 'Additional Citizenship', value: formData.otherCitizenship ? 'Yes' : 'No' },
  ];
}

function getContactFields(formData: FormData): LabeledField[] {
  return [
    { label: 'Company Name', value: formData.companyName || 'N/A' },
    { label: 'Email', value: formData.email || 'N/A' },
    { label: 'Phone', value: formData.phone || 'N/A' },
    { label: 'Mobile', value: formData.mobile || 'N/A' },
  ];
}

function getAddressFields(formData: FormData): LabeledField[] {
  return [
    { label: 'Care Of', value: formData.careOf || 'N/A' },
    { label: 'Street Address', value: formData.streetAddress || 'N/A' },
    { label: 'Postal Code', value: formData.postalCode || 'N/A' },
    { label: 'City', value: formData.city || 'N/A' },
  ];
}

async function createFallbackResources(pdfDoc: PDFDocument): Promise<DrawTextResources> {
  return {
    font: await pdfDoc.embedFont(StandardFonts.Helvetica),
    boldFont: await pdfDoc.embedFont(StandardFonts.HelveticaBold),
    italicFont: await pdfDoc.embedFont(StandardFonts.HelveticaOblique),
  };
}

export const PdfService = {
  async generateRegistrationPdf(formData: FormData): Promise<Uint8Array> {
    try {
      const templateBytes = await fetchTemplateBytes();

      if (templateBytes === null) {
        return this.generateFallbackPdf(formData);
      }

      const pdfDoc = await PDFDocument.load(templateBytes);

      try {
        fillRegistrationForm(pdfDoc.getForm(), formData);
      } catch (error) {
        console.error('Error filling PDF form fields:', error);
      }

      return await pdfDoc.save();
    } catch (error) {
      console.error('Error loading or processing PDF template:', error);
      return this.generateFallbackPdf(formData);
    }
  },

  async generateFallbackPdf(formData: FormData): Promise<Uint8Array> {
    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([595.28, 841.89]);
    const resources = await createFallbackResources(pdfDoc);
    const context = buildFallbackContext(page, resources);

    drawFallbackHeader(context);

    let yPosition = drawSectionTitle(context, 'Contact Person Information', 740);
    yPosition = drawFallbackDescription(context, yPosition);
    yPosition = drawFallbackSubsectionTitle(context, 'Personal Details', yPosition);
    yPosition = drawTwoColumnFields(context, getPersonalFields(formData), yPosition);

    if (formData.otherCitizenship) {
      page.drawText('Citizenship Countries:', {
        x: 70,
        y: yPosition,
        size: context.fontSize,
        font: context.boldFont,
        color: TEXT_BLACK,
      });
      page.drawText(formData.citizenshipCountries || 'N/A', {
        x: 190,
        y: yPosition,
        size: context.fontSize,
        font: context.font,
        color: TEXT_BLACK,
      });
      yPosition -= 40;
    }

    yPosition = drawFallbackSubsectionTitle(context, 'Contact Information', yPosition);
    yPosition = drawTwoColumnFields(context, getContactFields(formData), yPosition);
    yPosition = drawFallbackSubsectionTitle(context, 'Address Information', yPosition);
    drawTwoColumnFields(context, getAddressFields(formData), yPosition);
    drawFallbackFooter(context);

    return await pdfDoc.save();
  },
};
